import React from 'react';
import { Plus, ExternalLink, CreditCard, Search, Users, FileText } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import { apolloUrlProblem, normalizeApolloUrl } from '../../utils/apolloUrl';

const JobCreateForm = ({
  formData,
  setFormData,
  creating,
  error,
  subscription,
  contactLimits = { min_contacts: 400, max_contacts: 50000 },
  onSubmit,
  onCancel,
  variant = 'classic'
}) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const estimatedCredits = parseInt(formData.numberOfContacts) || 0;
  const hasEnoughCredits = subscription && subscription.credits_remaining >= estimatedCredits;
  // Same rule as the server, so a URL it would refuse is caught here
  const isValidUrl = Boolean(formData.apolloUrl) && !apolloUrlProblem(normalizeApolloUrl(formData.apolloUrl));
  const hasUniqueUrlId = formData.apolloUrl && formData.apolloUrl.includes('uniqueUrlId');
  const hasSearchSignals = formData.apolloUrl && formData.apolloUrl.includes('searchSignalIds[]=');
  const hasPersonas = formData.apolloUrl && formData.apolloUrl.includes('qPersonPersonaIds');
  const hasContactLabels = formData.apolloUrl && formData.apolloUrl.includes('contactLabelIds');
  const isValidContactCount = estimatedCredits >= contactLimits.min_contacts && estimatedCredits <= contactLimits.max_contacts;

  const isFocused = variant === 'focused';


  return (
    <Card style={isFocused ? { padding: 24 } : undefined}>
      {isFocused ? (
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap', marginBottom: 20
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--gradient-brand)',
              color: 'var(--color-accent-contrast)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <Search size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-text-primary)', margin: 0, marginBottom: 3 }}>
                New Scraping Request
              </h3>
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>
                Extract leads from Apollo.io with our advanced scraping technology
              </p>
            </div>
          </div>
        </div>
      ) : (
        <Card.Header>
          <Card.Title style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={20} color="#667eea" />
            Create New Scraping Job
          </Card.Title>
        </Card.Header>
      )}

      <Card.Content>
        {error && (
          <div style={{
            backgroundColor: 'var(--color-danger-bg)',
            border: '1px solid var(--color-danger-border)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            color: 'var(--color-danger-hover)'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          {isFocused ? (
            <>
              <FormField
                label="Apollo Search URL"
                labelHint="Paste the URL of your Apollo.io people search"
                icon={<ExternalLink size={15} />}
                type="url"
                name="apolloUrl"
                value={formData.apolloUrl}
                onChange={handleInputChange}
                required
                placeholder="https://app.apollo.io/#/people?..."
              />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
                <FormField
                  label="Number of Leads"
                  icon={<Users size={15} />}
                  type="number"
                  name="numberOfContacts"
                  value={formData.numberOfContacts}
                  onChange={handleInputChange}
                  required
                  min={contactLimits.min_contacts}
                  max={contactLimits.max_contacts}
                  placeholder={contactLimits.min_contacts.toString()}
                  style={{ marginBottom: 4 }}
                />
                <FormField
                  label="File Name"
                  icon={<FileText size={15} />}
                  type="text"
                  name="listName"
                  value={formData.listName}
                  onChange={handleInputChange}
                  required
                  placeholder="tech_leads_q1"
                  style={{ marginBottom: 4 }}
                />
              </div>

              {/* Replaces the classic layout's blue info panel. */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                fontSize: 13,
                color: 'var(--color-text-muted)',
                marginBottom: 20
              }}>
                <span>Min: {contactLimits.min_contacts.toLocaleString()} leads</span>
                <span>Max: {contactLimits.max_contacts.toLocaleString()} leads</span>
              </div>
            </>
          ) : (
          <>
          <FormField
            label="List Name"
            type="text"
            name="listName"
            value={formData.listName}
            onChange={handleInputChange}
            required
            placeholder="e.g., Tech Companies in SF"
          />
          
          <FormField
            label="Apollo.io URL"
            type="url"
            name="apolloUrl"
            value={formData.apolloUrl}
            onChange={handleInputChange}
            required
            placeholder="https://app.apollo.io/people?q=..."
          />
          </>
          )}
          
          {formData.apolloUrl && !isValidUrl && (
            <div style={{
              backgroundColor: 'var(--color-warning-bg-strong)',
              border: '1px solid #fed7aa',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              color: 'var(--color-warning-hover)',
              fontSize: '14px'
            }}>
              ⚠️ Please provide a valid Apollo.io URL that contains "/people?" (e.g., from Apollo's People search)
            </div>
          )}
          
          {formData.apolloUrl && hasUniqueUrlId && (
            <div style={{
              backgroundColor: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px',
              color: 'var(--color-danger-hover)',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🚫</span>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                    Apollo.io Filter 2.0 Not Supported
                  </div>
                  <div style={{ marginBottom: '12px', lineHeight: '1.4' }}>
                    We detected that you're using Apollo's new Filter 2.0 system (contains "uniqueUrlId").
                    Our scraper currently supports only <strong>Filters 1.0</strong>.
                  </div>
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid var(--color-bg-inset)',
                    borderRadius: '4px',
                    padding: '10px',
                    fontSize: '13px',
                    color: 'var(--color-text-secondary)'
                  }}>
                    <strong>How to fix:</strong><br/>
                    1. Go to your Apollo.io search page<br/>
                    2. Look for the "Filters 1.0" option (usually near the filter controls)<br/>
                    3. Switch back to <strong>Filters 1.0</strong><br/>
                    4. Copy the URL again and paste it here
                  </div>
                </div>
              </div>
            </div>
          )}

          {formData.apolloUrl && hasSearchSignals && (
            <div style={{
              backgroundColor: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px',
              color: 'var(--color-danger-hover)',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🚫</span>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                    Apollo.io Search Signals Filter Not Supported
                  </div>
                  <div style={{ marginBottom: '12px', lineHeight: '1.4' }}>
                    We detected that you're using Apollo's Search Signals filter.
                    This filter is currently not supported by our scraper.
                  </div>
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid var(--color-bg-inset)',
                    borderRadius: '4px',
                    padding: '10px',
                    fontSize: '13px',
                    color: 'var(--color-text-secondary)'
                  }}>
                    <strong>How to fix:</strong><br/>
                    1. Go to your Apollo.io search page<br/>
                    2. Remove the <strong>Search Signals</strong> filter from your search<br/>
                    3. Copy the URL again and paste it here
                  </div>
                </div>
              </div>
            </div>
          )}

          {formData.apolloUrl && hasPersonas && (
            <div style={{
              backgroundColor: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px',
              color: 'var(--color-danger-hover)',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🚫</span>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                    Apollo.io Personas Filter Not Supported
                  </div>
                  <div style={{ marginBottom: '12px', lineHeight: '1.4' }}>
                    We detected that you're using Apollo's Personas filter.
                    This filter is currently not supported by our scraper.
                  </div>
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid var(--color-bg-inset)',
                    borderRadius: '4px',
                    padding: '10px',
                    fontSize: '13px',
                    color: 'var(--color-text-secondary)'
                  }}>
                    <strong>How to fix:</strong><br/>
                    1. Go to your Apollo.io search page<br/>
                    2. Remove the <strong>Personas</strong> filter from your search<br/>
                    3. Copy the URL again and paste it here
                  </div>
                </div>
              </div>
            </div>
          )}

          {formData.apolloUrl && hasContactLabels && (
            <div style={{
              backgroundColor: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px',
              color: 'var(--color-danger-hover)',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🚫</span>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                    Apollo.io Contact Labels Filter Not Supported
                  </div>
                  <div style={{ marginBottom: '12px', lineHeight: '1.4' }}>
                    We detected that you're using Apollo's Contact Labels filter.
                    This filter is currently not supported by our scraper.
                  </div>
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid var(--color-bg-inset)',
                    borderRadius: '4px',
                    padding: '10px',
                    fontSize: '13px',
                    color: 'var(--color-text-secondary)'
                  }}>
                    <strong>How to fix:</strong><br/>
                    1. Go to your Apollo.io search page<br/>
                    2. Remove the <strong>Contact Labels</strong> filter from your search<br/>
                    3. Copy the URL again and paste it here
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isFocused && (
          <FormField
            label={`Number of Contacts (Min: ${contactLimits.min_contacts})`}
            type="number"
            name="numberOfContacts"
            value={formData.numberOfContacts}
            onChange={handleInputChange}
            required
            min={contactLimits.min_contacts}
            max={contactLimits.max_contacts}
            placeholder={contactLimits.min_contacts.toString()}
          />
          )}

          {/* Informational message about minimum contacts. Focused shows a
              compact Min/Max line under the fields instead. */}
          {!isFocused && (
          <div style={{
            backgroundColor: 'var(--color-info-bg)',
            border: '1px solid var(--color-info-border)',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px',
            color: 'var(--color-info-fg)',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <span style={{ fontSize: '16px', flexShrink: 0 }}>ℹ️</span>
            <span>
              URLs with less than {contactLimits.min_contacts} contacts will not be processed. Please ensure your Apollo.io search results contain at least {contactLimits.min_contacts} contacts.
            </span>
          </div>
          )}

          {/* Validation message for contact count */}
          {formData.numberOfContacts && !isValidContactCount && (
            <div style={{
              backgroundColor: 'var(--color-warning-bg-strong)',
              border: '1px solid #fed7aa',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              color: 'var(--color-warning-hover)',
              fontSize: '14px'
            }}>
              ⚠️ Number of contacts must be between {contactLimits.min_contacts} and {contactLimits.max_contacts}
            </div>
          )}

          {/* Credit Usage Info. Omitted in focused - the header pill and its
              dropdown carry the credit figures. */}
          {!isFocused && (
          <div style={{
            backgroundColor: subscription?.status === 'active' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
            border: `1px solid ${subscription?.status === 'active' ? 'var(--color-success-border)' : 'var(--color-danger-border)'}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <CreditCard size={16} color={subscription?.status === 'active' ? 'var(--color-success-fg)' : 'var(--color-danger-hover)'} />
              <span style={{
                fontWeight: '600',
                color: subscription?.status === 'active' ? 'var(--color-success-fg)' : 'var(--color-danger-hover)'
              }}>
                Credit Usage
              </span>
            </div>
            
            <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              <p style={{ margin: 0, marginBottom: '4px' }}>
                <strong>Estimated credits needed:</strong> {estimatedCredits.toLocaleString()}
              </p>
              {/* Focused layout shows available credits in the header pill instead. */}
              {!isFocused && (
                <p style={{ margin: 0, marginBottom: '8px' }}>
                  <strong>Available credits:</strong> {subscription?.credits_remaining?.toLocaleString() || 0}
                </p>
              )}
              
              {!isFocused && subscription && (
                <div style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  paddingTop: '8px',
                  borderTop: '1px solid var(--color-border)'
                }}>
                  Plan: {subscription.plan_name} | 
                  Status: {subscription.status} | 
                  {(() => {
                    if (!subscription.end_date) return 'No expiry date';
                    
                    const expiryDate = new Date(subscription.end_date);
                    const today = new Date();
                    const expiryDateOnly = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
                    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const formattedDate = new Date(subscription.end_date).toLocaleDateString('en-GB');
                    
                    if (expiryDateOnly < todayOnly) {
                      return `Expired on ${formattedDate}`;
                    } else if (expiryDateOnly.getTime() === todayOnly.getTime()) {
                      return `Expires today`;
                    } else {
                      return `Expires: ${formattedDate}`;
                    }
                  })()}
                </div>
              )}
            </div>
          </div>
          )}

          {!hasEnoughCredits && subscription && (
            <div style={{
              backgroundColor: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              color: 'var(--color-danger-hover)'
            }}>
              <p style={{ margin: 0, fontWeight: '600', marginBottom: '8px' }}>
                Insufficient Credits
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                You need {estimatedCredits.toLocaleString()} credits but only have {subscription.credits_remaining.toLocaleString()} remaining.
                Please contact support to upgrade your plan.
              </p>
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            paddingTop: '20px',
            borderTop: '1px solid var(--color-border)'
          }}>
            {onCancel && (
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={creating}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              style={isFocused ? { minWidth: 280, justifyContent: 'center' } : undefined}
              disabled={creating || !hasEnoughCredits || !isValidUrl || hasUniqueUrlId || hasSearchSignals || hasPersonas || hasContactLabels || !isValidContactCount}
              loading={creating}
              icon={isFocused ? <Search size={16} /> : <ExternalLink size={16} />}
            >
              {creating ? (isFocused ? 'Starting...' : 'Creating Job...') : (isFocused ? 'Start Scraping' : 'Create Job')}
            </Button>
          </div>
        </form>
      </Card.Content>
    </Card>
  );
};

export default JobCreateForm;